// src/components/promo/PromoRedeemModalHost.js
//
// Always-mounted, route-agnostic host for the promo-redeem modal. It:
//   1. Captures a ?promo=CODE query param on any screen (and strips it from the
//      URL) so the code survives the sign-up detour.
//   2. On the first landing after a fresh signup (one-shot armed flag), opens the
//      redeem modal pre-filled with the captured code.
//
// Because it keys on the armed flag, an already-signed-in user opening a promo
// link will NOT auto-open the modal — redemption is gated to post-signup.
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import {
  capturePromoCodeFromSearch,
  clearPromoRedemption,
  consumeSignupArm,
  readPendingPromoCode,
} from "../../utils/promoRedemption";
import { hasActivePromoCampaigns } from "../../services/promoCodeService";
import PromoRedeemModal from "./PromoRedeemModal";

export default function PromoRedeemModalHost() {
  const location = useLocation();
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || null;

  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");

  // 1) Bank a ?promo=CODE from any screen so it survives the sign-up detour.
  //    We leave the param in the URL — the one-shot armed flag (below) is what
  //    prevents re-triggering, so there's no need to scrub it.
  useEffect(() => {
    capturePromoCodeFromSearch(location.search);
  }, [location.search]);

  // 2) One-shot: open the modal on the first landing after a fresh signup.
  //    • With a pending ?promo code → open pre-filled on the offer.
  //    • Without one → open in manual-entry mode, but only if a promo campaign
  //      is actually redeemable (so we don't prompt for nothing).
  useEffect(() => {
    if (!uid || open) return;
    if (!consumeSignupArm()) return; // not a fresh signup landing

    const pending = readPendingPromoCode();
    if (pending) {
      setCode(pending);
      setOpen(true);
      return;
    }

    // consumeSignupArm() already cleared the one-shot flag, so this async check
    // runs at most once per signup even under StrictMode's double-invoke.
    hasActivePromoCampaigns().then((hasActive) => {
      if (hasActive) {
        setCode("");
        setOpen(true);
      }
    });
    // Re-evaluate when auth resolves or the route changes post-signup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, location.key]);

  const handleClose = useCallback(() => {
    setOpen(false);
    clearPromoRedemption();
  }, []);

  // `code` is intentionally empty in manual-entry mode, so gate on `open` alone.
  if (!open) return null;

  return <PromoRedeemModal open={open} code={code} onClose={handleClose} />;
}
